import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Collapse
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as TransactionsIcon,
  TrendingUp as MarginIcon,
  VerifiedUser as KycIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  AssignmentInd as KycUsersIcon,
  ManageAccounts as ManagerRolesIcon,
  History as HistoryIcon,
  ShowChart as PositionsIcon,
  NotificationsActive as TriggersIcon,
  Percent as ApyIcon,
  CompareArrows as CompareArrowsIcon,
  CurrencyExchange as CurrencyIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon,
  Settings as InstrumentsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  permission?: string;
  subItems?: SubMenuItem[];
}

interface SubMenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  permission?: string;
}

const menuItems: MenuItem[] = [
  { text: 'Дашборд', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Аналитика', icon: <AnalyticsIcon />, path: '/analytics' },
  { text: 'Инструменты', icon: <InstrumentsIcon />, path: '/instruments' },
  { text: 'Пользователи', icon: <PeopleIcon />, path: '/users', permission: 'users.read' },
  { text: 'Кошельки', icon: <WalletIcon />, path: '/wallets', permission: 'wallets.read' },
  { text: 'Транзакции', icon: <TransactionsIcon />, path: '/transactions', permission: 'transactions.read' },
  { 
    text: 'Маржинальная торговля', 
    icon: <MarginIcon />, 
    path: '/margin', 
    permission: 'margin.read',
    subItems: [
      { text: 'Позиции', icon: <PositionsIcon />, path: '/margin/positions' },
      { text: 'Триггеры', icon: <TriggersIcon />, path: '/margin/triggers' },
      { text: 'APY доходы', icon: <ApyIcon />, path: '/margin/apy' },
      { text: 'Контроль курса', icon: <CurrencyIcon />, path: '/margin/course' },
      { text: 'Кредиторы', icon: <PersonIcon />, path: '/margin/creditors' }
    ]
  },
  { 
    text: 'KYC', 
    icon: <KycIcon />, 
    path: '/kyc', 
    permission: 'kyc.read',
    subItems: [
      { text: 'Запросы', icon: <KycIcon />, path: '/kyc' },
      { text: 'Пользователи', icon: <KycUsersIcon />, path: '/kyc/users' }
    ]
  },
  {
    text: 'Обмены',
    icon: <CompareArrowsIcon />,
    path: '/exchanges',
    subItems: [
      { text: 'Кастодиальные', icon: <CompareArrowsIcon />, path: '/exchanges/custody' },
      { text: 'Некастодиальные', icon: <CompareArrowsIcon />, path: '/exchanges/non-custody' }
    ]
  },
  { text: 'Журнал аудита', icon: <HistoryIcon />, path: '/audit_logs', permission: 'audit.read' },
  { text: 'Настройки', icon: <SettingsIcon />, path: '/settings', permission: 'settings.read' },
  { text: 'Роли менеджеров', icon: <ManagerRolesIcon />, path: '/manager_roles', permission: 'roles.read' }
];

const MainMenu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const [openSubMenu, setOpenSubMenu] = React.useState<string | null>('KYC');

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleToggleSubMenu = (text: string) => {
    setOpenSubMenu(openSubMenu === text ? null : text);
  };

  return (
    <List>
      {menuItems.map((item) => {
        // Проверяем права доступа, если указаны
        if (item.permission && !hasPermission(item.permission)) {
          return null;
        }
        
        const isActive = location.pathname === item.path;
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isSubMenuOpen = openSubMenu === item.text;
        const isSubItemActive = hasSubItems && item.subItems?.some(subItem => location.pathname.startsWith(subItem.path));
        
        return (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton 
                onClick={() => hasSubItems ? handleToggleSubMenu(item.text) : handleNavigate(item.path)}
                selected={isActive || isSubItemActive}
              >
                <ListItemIcon sx={{ color: (isActive || isSubItemActive) ? 'primary.main' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{ 
                    fontWeight: (isActive || isSubItemActive) ? 'bold' : 'normal',
                    color: (isActive || isSubItemActive) ? 'primary.main' : 'inherit'
                  }} 
                />
                {hasSubItems && (isSubMenuOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>
            
            {hasSubItems && (
              <Collapse in={isSubMenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems?.map((subItem) => {
                    const isSubActive = location.pathname === subItem.path;
                    return (
                      <ListItemButton 
                        key={subItem.text}
                        sx={{ pl: 4 }}
                        onClick={() => handleNavigate(subItem.path)}
                        selected={isSubActive}
                      >
                        <ListItemIcon sx={{ color: isSubActive ? 'primary.main' : 'inherit' }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={subItem.text} 
                          primaryTypographyProps={{ 
                            fontWeight: isSubActive ? 'bold' : 'normal',
                            color: isSubActive ? 'primary.main' : 'inherit'
                          }} 
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default MainMenu; 